import React, { ChangeEvent, useState, useEffect } from "react";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { BasePopup } from "components/pop-up/BasePopup";
import { Referral } from "types";
import { updateAssetReferral } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";

interface CreateTerminationRequestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (e: React.FormEvent) => void;
  isEditMode?: boolean;
  existingData?: any;
  referralId: string;
  referral: Referral | null;
  onTerminationCreated?: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

const defaultFormData = {
  termination_reason: "",
  termination_date: "",
};

export const CreateTerminationRequestPopup: React.FC<
  CreateTerminationRequestPopupProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode = false,
  existingData,
  referralId,
  setActiveTab,
  onTerminationCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState(defaultFormData);

  const { t } = useTranslation();
  const { showTooltip } = useTooltip();

  const actualEditMode = isEditMode || Boolean(existingData);

  useEffect(() => {
    if (isOpen && actualEditMode && existingData) {
      console.log("Initializing edit mode with data:", existingData);
      // Populate form with existing data
      setFormData({
        termination_reason: existingData.termination_reason || "",
        termination_date: existingData.termination_date || "",
      });
    } else if (isOpen && !actualEditMode) {
      // Reset form for new entry
      console.log("Initializing create mode");
      resetForm();
    }
  }, [isOpen, actualEditMode, existingData]);

  const resetForm = () => {
    setFormData(defaultFormData);
    setUploadedFiles([]);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.termination_reason.trim()) {
      showTooltip(t("please_enter_termination_reason"), "error");
      return;
    }

    if (!formData.termination_date) {
      showTooltip(t("please_select_termination_date"), "error");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Append form data
      data.append("termination_reason", formData.termination_reason);
      data.append("termination_date", formData.termination_date);

      // Set appropriate status based on edit mode
      if (actualEditMode) {
        data.append("action_status", "Termination Updated");
        // data.append("forward_status", "Forwarded to Coordinator");
      } else {
        data.append("action_status", "Termination Requested");
        // data.append("forward_status", "Forwarded to Coordinator");
        data.append("is_termination_requested", "true");
      }

      // Append files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          data.append("termination_order_document", file);
        });
      }

      // Update the referral with termination data
      const response = await updateAssetReferral(referralId, data);

      const successMessage = actualEditMode
        ? "Termination request updated successfully!"
        : "Termination request created successfully!";

      showTooltip(successMessage, "success");
      onClose();
      resetForm();

      if (onTerminationCreated && response) {
        onTerminationCreated(response);
        setActiveTab?.("Terminate");
      }
    } catch (error) {
      const errorMessage = actualEditMode
        ? "Failed to update termination request"
        : "Failed to create termination request";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        actualEditMode
          ? "Edit Termination Request"
          : "Create Termination Request"
      }
      description={
        actualEditMode
          ? "Edit termination request information"
          : "Create a new termination request for this referral"
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <label className="referral-popup-label">Termination Reason *</label>
          <textarea
            className="referral-form-field h-24"
            onChange={handleInputChange}
            name="termination_reason"
            placeholder="Enter detailed termination reason..."
            value={formData.termination_reason}
            required
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Termination Date *</label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="termination_date"
            type="date"
            value={formData.termination_date}
            required
          />
        </div>

        <div className="mt-4">
          <MultipleFileUploadComponent
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            label="Termination Order Documents"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 referral-form-button text-white w-full ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading
            ? "Processing..."
            : actualEditMode
              ? "Update Termination Request"
              : "Create Termination Request"}
        </button>
      </form>
    </BasePopup>
  );
};

export default CreateTerminationRequestPopup;
